import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDispatch } from "src/store";
import useFetchInterval from "src/ui/hooks/useFetchInterval";

import { createDeviceMdmCommand, deleteDeviceMdmCommand, fetchDeviceMdmCommands } from "@dashboard/devices/api/devices";
import { selectDetailsLicense, selectPaneTab } from "@dashboard/devices/store/selectors";
import { MdmCommand } from "@dashboard/devices/types/commands";
import { hasUnfinishedCommands } from "@dashboard/shared/components/SidePanel/shared/CommandTab/CommandTab.utils";
import { workspacesApi } from "@dashboard/workspaces/api";
import { pageIndexOffset } from "@shared/table/Tanstack.utils";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { SortingState } from "@tanstack/react-table";

function getSortCriteria(sorting: SortingState) {
    if (!sorting.length) {
        return "created";
    }
    const mapping: Record<string, string> = {
        date: "created",
        status: "status",
        origin: "origin",
        command: "caption",
    };
    return mapping[sorting[0].id] || "created";
}

function getSortDirection(sorting: SortingState) {
    if (!sorting.length || sorting[0].desc) {
        return "descending";
    }
    return "ascending";
}

const rowsPerPage = [10, 25, 50];

type DeviceCommandContextType = {
    data: MdmCommand[];
    totalCount: number;
    isLoading: boolean;
    deletingId: number | null;
    resendingId: number | null;
    pageIndex: number;
    sorting: SortingState;
    pageSize: number;
    rowsPerPage: number[];
    onCreate: (json: Record<string, unknown>, commandId: number | null) => Promise<void>;
    onDelete: (command: MdmCommand) => Promise<void>;
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
    setPageIndex: React.Dispatch<React.SetStateAction<number>>;
    setPageSize: React.Dispatch<React.SetStateAction<number>>;
};

const DeviceCommandContext = React.createContext<DeviceCommandContextType>({
    data: [],
    totalCount: 0,
    isLoading: false,
    deletingId: null,
    resendingId: null,
    sorting: [{ id: "date", desc: true }],
    pageIndex: 0,
    pageSize: rowsPerPage[0],
    rowsPerPage,
    onCreate: () => Promise.resolve(),
    onDelete: () => Promise.resolve(),
    setSorting: () => {},
    setPageIndex: () => {},
    setPageSize: () => {},
});

const refreshableTabs = ["COMMANDS", "BWC"];

export const DeviceCommandProvider = (props: React.PropsWithChildren) => {
    const [sorting, setSorting] = useState<SortingState>([{ id: "date", desc: true }]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(rowsPerPage[0]);
    const [data, setData] = useState<MdmCommand[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [resendingId, setResendingId] = useState<number | null>(null);

    const { t } = useTranslation(["common", "sidepanels"]);
    const dispatch = useDispatch();
    const hasUnfinishedCommand = hasUnfinishedCommands(data);

    const license = useSelector(selectDetailsLicense);
    const sortCriteria = getSortCriteria(sorting);
    const sortDirection = getSortDirection(sorting);

    const selectedTab = useSelector(selectPaneTab);

    const onFetch = useCallback(async () => {
        if (license?.device?.id) {
            try {
                setIsLoading(true);

                const response = await fetchDeviceMdmCommands({
                    deviceId: license.device.id,
                    pageSize,
                    pageIndex: pageIndex + pageIndexOffset,
                    sortCriteria,
                    sortDirection,
                });
                setData(response.list);
                setTotalCount(response.count);
            } catch (_error) {
                setData([]);
                setTotalCount(0);
            } finally {
                setIsLoading(false);
            }
        }
    }, [license?.device?.id, pageSize, pageIndex, sortCriteria, sortDirection]);
    const onRefresh = useCallback(async () => {
        if (license?.device?.id) {
            try {
                const response = await fetchDeviceMdmCommands({
                    deviceId: license.device.id,
                    pageSize,
                    pageIndex: pageIndex + pageIndexOffset,
                    sortCriteria,
                    sortDirection,
                });
                setData(response.list);
                setTotalCount(response.count);
            } catch (_error) {
                setData([]);
                setTotalCount(0);
            }
        }
    }, [license?.device?.id, pageSize, pageIndex, sortCriteria, sortDirection]);
    const onCreate = async (json: Record<string, unknown>, commandId: number | null) => {
        if (license?.device?.id) {
            try {
                setResendingId(commandId);

                const newCommands = await createDeviceMdmCommand({ deviceId: license.device.id, json });
                if (newCommands) {
                    setData((prev) => [newCommands[0], ...prev]);
                    setTotalCount((prev) => prev + 1);
                    dispatch(workspacesApi.util.invalidateTags(["WorkspaceFeatures"]));
                    showSuccessToast(t("common:toast.commandEnqueued"));
                }
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message || t("sidepanels:devicePanel.toast.commandSentFailed"));
            } finally {
                setResendingId(null);
            }
        }
    };
    const onDelete = async (command: MdmCommand) => {
        if (license?.device?.id) {
            try {
                setDeletingId(command.id);
                const response = await deleteDeviceMdmCommand({ deviceId: license.device?.id, commandId: command.id });
                if (response) {
                    setData((prev) => prev.filter((cmd) => cmd.id !== command.id));
                    setTotalCount((prev) => prev - 1);
                    dispatch(workspacesApi.util.invalidateTags(["WorkspaceFeatures"]));
                    showSuccessToast(t("sidepanels:devicePanel.toast.commandDeletedSuccessfully"));
                }
            } catch (err) {
                const error = err as Error;
                showErrorToast(error.message || t("sidepanels:devicePanel.toast.commandDeletedFailed"));
            } finally {
                setDeletingId(null);
            }
        }
    };

    React.useEffect(() => {
        void onFetch();
    }, [onFetch]);

    React.useEffect(() => {
        if (refreshableTabs.includes(selectedTab)) {
            void onRefresh();
        }
    }, [selectedTab, onRefresh]);

    useFetchInterval(onRefresh, {
        frequency: 10000,
        condition: hasUnfinishedCommand && refreshableTabs.includes(selectedTab),
    });

    return (
        <DeviceCommandContext.Provider
            value={{
                data,
                totalCount,
                isLoading,
                deletingId,
                resendingId,
                sorting,
                pageIndex,
                pageSize,
                rowsPerPage,
                onCreate,
                onDelete,
                setSorting,
                setPageIndex,
                setPageSize,
            }}
        >
            {props.children}
        </DeviceCommandContext.Provider>
    );
};

export const useDeviceCommand = () => {
    const context = useContext(DeviceCommandContext);
    if (!context) {
        throw new Error("useDeviceCommand must be used within a DeviceCommandProvider");
    }
    return context;
};
